import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getGoogleMapsKey } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

// Module-level singleton so the script is only loaded once per session
let scriptLoadPromise = null;
let loadedApiKey      = null;

function loadGoogleMapsScript(apiKey) {
  if (window.google?.maps?.places) return Promise.resolve();
  if (scriptLoadPromise && loadedApiKey === apiKey) return scriptLoadPromise;

  loadedApiKey = apiKey;
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-maps-script');
    if (existing) { existing.remove(); }

    const script = document.createElement('script');
    script.id  = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload  = resolve;
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * AddressAutocomplete — drop-in Google Places address autocomplete.
 *
 * Props:
 *   value         string              — controlled input value (street address)
 *   onChange      (street) => void    — called when street changes (typed or autocompleted)
 *   onPlaceSelect ({ street, city, state, zip, lat, lng }) => void
 *                                     — called when user picks a suggestion
 *   placeholder   string
 *   className     string              — defaults to "form-input"
 *   style         object
 *   disabled      boolean
 *   name          string              — input name attr
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  onPlaceSelect,
  placeholder = 'Start typing an address…',
  className = 'form-input',
  style,
  disabled,
  name,
}) {
  const { user } = useAuth();
  const inputRef      = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady]   = useState(false);
  const [error, setError]   = useState(null);

  // Only attempt to load if org has the feature.
  // Checks both field names — has_google_maps is set by mapOrg on the server,
  // plan_has_google_maps is a fallback from enrichOrgWithPlan.
  const org = user?.organization;
  const hasFeature = org?.has_google_maps === true
    || org?.plan_has_google_maps === true;

  const initAutocomplete = useCallback(() => {
    if (!inputRef.current || autocompleteRef.current) return;
    if (!window.google?.maps?.places) return;

    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types:              ['address'],
      componentRestrictions: { country: 'us' },
      fields:             ['address_components', 'geometry', 'formatted_address'],
    });

    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.address_components) return;

      const get = (type, short = false) => {
        const comp = place.address_components.find(c => c.types.includes(type));
        return comp ? (short ? comp.short_name : comp.long_name) : '';
      };

      const streetNumber = get('street_number');
      const streetName   = get('route');
      const street       = [streetNumber, streetName].filter(Boolean).join(' ');
      const city         = get('locality') || get('sublocality') || get('neighborhood');
      const state        = get('administrative_area_level_1', true);
      const zip          = get('postal_code');
      const lat          = place.geometry?.location?.lat();
      const lng          = place.geometry?.location?.lng();

      if (onChange)      onChange(street || place.formatted_address || '');
      if (onPlaceSelect) onPlaceSelect({ street: street || place.formatted_address || '', city, state, zip, lat, lng });
    });

    autocompleteRef.current = ac;
  }, [onChange, onPlaceSelect]);

  useEffect(() => {
    if (!hasFeature || disabled) return;

    let cancelled = false;

    async function load() {
      try {
        const { api_key } = await getGoogleMapsKey();
        if (cancelled) return;
        await loadGoogleMapsScript(api_key);
        if (cancelled) return;
        setReady(true);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Google Maps unavailable');
      }
    }
    load();

    return () => { cancelled = true; };
  }, [hasFeature, disabled]);

  // Attach autocomplete after script is ready
  useEffect(() => {
    if (ready) initAutocomplete();
  }, [ready, initAutocomplete]);

  // Fall back to plain input if feature not enabled or error
  return (
    <input
      ref={inputRef}
      type="text"
      name={name}
      className={className}
      style={style}
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      placeholder={ready ? 'Start typing an address…' : placeholder}
      disabled={disabled}
      autoComplete={ready ? 'off' : 'street-address'} // disable browser autocomplete when Google is active
    />
  );
}
